import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { CustomProduct, ProductBonus } from '@product/const';
import { mapBonuses } from '@product/utils';
import { AuthService } from '@spartacus/core';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { CustomProductActions } from '../store/actions';
import { StateWithProduct } from '../store/product-state';
import { ProductSelectors } from '../store/selectors';
import { SelectedPriceService } from './selected-price.service';

type ProductItem = Pick<CustomProduct, 'multiunit' | 'code' | 'unit' | 'unitPrices' | 'units' | 'price' | 'bonus'>;

@Injectable({ providedIn: 'root' })
export class ProductBonusService {
  private previousAuthState = new Map<string, boolean>();

  constructor(
    private store: Store<StateWithProduct>,
    private selectedPriceService: SelectedPriceService,
    private authService: AuthService,
  ) {}

  init(product: ProductItem): void {
    combineLatest({ productBonuses: this.getBonuses(product.code), isUserLoggedIn: this.authService.isUserLoggedIn() })
      .pipe(take(1))
      .subscribe(({ productBonuses, isUserLoggedIn }): void => {
        const shouldRefresh = this.shouldRefreshBonuses(product.code, isUserLoggedIn);

        if (productBonuses && !shouldRefresh) {
          return;
        }

        let bonuses: ProductBonus[] = [];

        // Если это Листинг, то бонусы берем из product.bonus.
        // Если избранное - они приходят в UnitPrices.bonuses
        if (product.bonus) {
          bonuses.push({ ...product.bonus, unit: product.unit });
          this.setBonuses(product.code, bonuses);
        } else if (product.unitPrices) {
          bonuses = mapBonuses(product.unitPrices);
          this.setBonuses(product.code, bonuses);
        }
      });
  }

  getBonuses(productCode: string): Observable<ProductBonus[]> {
    return this.store.select(ProductSelectors.getBonusesValue(productCode));
  }

  getBonusesBySelectedUnit(productCode: string): Observable<ProductBonus[]> {
    return combineLatest([
      this.selectedPriceService.getSelectedPrice(productCode),
      this.getBonuses(productCode).pipe(filter<ProductBonus[]>(Boolean)),
    ]).pipe(
      map(([selectedPrice, bonuses]) => {
        return bonuses.filter((bonus) => bonus.unit?.code === selectedPrice.unit?.code);
      }),
    );
  }

  private setBonuses(productCode: string, bonuses: ProductBonus[]): void {
    this.store.dispatch(new CustomProductActions.SetBonuses(productCode, bonuses));
  }

  private shouldRefreshBonuses(productCode: string, isUserLoggedIn: boolean): boolean {
    const previousState = this.previousAuthState.get(productCode);
    const authChanged = previousState !== undefined && previousState !== isUserLoggedIn;

    this.previousAuthState.set(productCode, isUserLoggedIn);

    return authChanged;
  }
}
